import { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import { MIN_MEMBERSHIP_SAVINGS } from 'constants/plusMembership';
import { JoinModal } from 'features/paid-membership/JoinModal';
import Tag from 'honeydew/Tag/Tag';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { getCartMemberSavings, getCartNonMemberSavings } from 'utils/priceUtils';

function BannerContainer({ children, className, intersectionRef, membership, setModalOpen, trial }) {
    return !!membership || !!trial ? (
        <div
            className={clsx(
                'relative flex w-full justify-center overflow-hidden rounded-lg bg-malt p-5 pt-10 if:bg-oat',
                className
            )}
            ref={intersectionRef}
        >
            {children}
        </div>
    ) : (
        <button
            className={clsx(
                'relative flex w-full justify-center overflow-hidden rounded-lg bg-malt p-5 pt-10 if:bg-oat',
                className
            )}
            onClick={() => setModalOpen(true)}
            ref={intersectionRef}
            type="button"
        >
            {children}
        </button>
    );
}

BannerContainer.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    intersectionRef: PropTypes.shape({}).isRequired,
    membership: PropTypes.shape({}),
    setModalOpen: PropTypes.func.isRequired,
    trial: PropTypes.shape({}),
};

BannerContainer.defaultProps = {
    membership: undefined,
    trial: undefined,
};

BannerContainer.defaultProps = {
    className: undefined,
};

function PlusMembershipStaticBanner({ className, intersectionRef }) {
    const [modalOpen, setModalOpen] = useState(false);

    const { data: account = {} } = useAccountQuery();
    const { data: selections = {} } = useSelectionsQuery();

    const { membership, plusMembershipBranding, plusMembershipShortBranding, trial } = useAccountPlusMembershipHook();

    const cartNonMemberSavings = getCartNonMemberSavings({ account, selections });
    const cartMemberSavings = getCartMemberSavings({ account, selections });

    const cartTotalSavings = cartNonMemberSavings + cartMemberSavings;

    if (!membership && !trial && cartMemberSavings < MIN_MEMBERSHIP_SAVINGS) {
        return false;
    }

    if ((!!membership || !!trial) && cartNonMemberSavings > 0 && cartMemberSavings < MIN_MEMBERSHIP_SAVINGS) {
        return false;
    }

    return (
        <>
            <BannerContainer
                className={className}
                data-testId="plus-membership-static-banner"
                membership={membership}
                intersectionRef={intersectionRef}
                setModalOpen={setModalOpen}
                trial={trial}
            >
                <div className="absolute left-0 top-0 z-10 h-5 w-full bg-gradient-berry-blend" />
                <div className="flex items-center justify-center gap-10">
                    <p className="text-body-sm w-fit text-center">
                        {!membership && !trial && cartMemberSavings >= MIN_MEMBERSHIP_SAVINGS && (
                            <span className="font-grotesk-bold">
                                Save an extra <span className="text-cranberry">${cartMemberSavings.toFixed(2)}</span> on
                                this order by signing up for {plusMembershipBranding}
                            </span>
                        )}
                        {(!!membership || !!trial) && cartMemberSavings >= MIN_MEMBERSHIP_SAVINGS && (
                            <>
                                <span className="font-grotesk-bold">
                                    You saved <span className="text-cranberry">${cartTotalSavings.toFixed(2)}</span> on
                                    this order
                                </span>
                                <br />${cartMemberSavings.toFixed(2)} is from your{' '}
                                <Tag bgColor="cranberry" fontSize="mobileXS" variant="dark">
                                    {plusMembershipShortBranding}
                                </Tag>{' '}
                                membership
                            </>
                        )}
                        {(!!membership || !!trial) &&
                            cartNonMemberSavings <= 0 &&
                            cartMemberSavings < MIN_MEMBERSHIP_SAVINGS && (
                                <span className="font-grotesk-bold">
                                    Thanks for being a <span className="text-cranberry">{plusMembershipBranding}</span>{' '}
                                    member!
                                </span>
                            )}
                    </p>
                    {!membership && !trial && (
                        <div className="shrink-0">
                            <ChevronRightIcon width={25} />
                        </div>
                    )}
                </div>
            </BannerContainer>
            <JoinModal onClose={() => setModalOpen(false)} open={modalOpen} />
        </>
    );
}

PlusMembershipStaticBanner.propTypes = {
    className: PropTypes.string,
    intersectionRef: PropTypes.shape({}).isRequired,
};

PlusMembershipStaticBanner.defaultProps = {
    className: undefined,
};

export { PlusMembershipStaticBanner };
